import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query'
import {
  getSearchTokens,
  getTokenAcrossChains,
  getTrendingTokens,
} from '@/src/services/tokens'

const TOKEN_SEARCH_PER_PAGE_DEFAULT = 40

const TOKENS_ROOT_QUERY_KEY = 'tokens' as const

export interface TokensSearchQueryProps {
  query?: string
  /** Default: 40 */
  perPage?: number
  chainId?: string
  /** Default: false */
  withPrice?: boolean
}

export const tokensSearchQuery = ({
  query = '',
  perPage = TOKEN_SEARCH_PER_PAGE_DEFAULT,
  chainId = '',
  withPrice = false,
}: TokensSearchQueryProps = {}) =>
  infiniteQueryOptions({
    queryKey: [
      TOKENS_ROOT_QUERY_KEY,
      'search',
      JSON.stringify({ query, perPage, chainId }),
    ],
    queryFn: ({ pageParam }) =>
      getSearchTokens({
        page: pageParam.toString(),
        query,
        'per-page': perPage.toString(),
        'chain-id': chainId ? chainId : undefined,
        'with-price': withPrice ? 'true' : undefined,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage?.assets?.length === perPage) {
        return lastPage.page + 1
      }

      return undefined
    },
    getPreviousPageParam: (firstPage) => {
      const page = firstPage.page - 1

      if (page <= 0) {
        return undefined
      }

      return undefined
    },
    placeholderData: (previousData) => previousData, // Keep previous search results visible while new ticker search loads
  })

export const tokenAcrossChainsQuery = (
  chain?: string | null,
  denom?: string | null,
) =>
  queryOptions({
    queryKey: [TOKENS_ROOT_QUERY_KEY, chain, denom],
    queryFn: () => getTokenAcrossChains({ chain: chain!, denom: denom! }),
    enabled: !!chain && !!denom,
    staleTime: 60 * 60 * 24, // 1 day
    placeholderData: (previousData) => previousData, // Keep previous token data visible while new token loads
  })

export const trendingTokensQuery = () =>
  queryOptions({
    queryKey: [TOKENS_ROOT_QUERY_KEY, 'trending-assets'],
    queryFn: () => getTrendingTokens(),
    // Aligned with backend cache: 5min (app/registryprovider/registryprovider.go:116 - assetPairsRefreshInterval)
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
